import {
  OrgRoles,
  OrganizationInviteRequest,
  OrganizationService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { CaretSortIcon } from '@radix-ui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

// Define the roles dropdown
type orgRolesDropdownItem = {
  value: OrgRoles;
  description: string;
};
const orgRoles: orgRolesDropdownItem[] = [
  { value: 'Member', description: 'Read only access to Organization' },
  { value: 'Admin', description: 'Organization Management' },
  { value: 'Owner', description: 'Organization Management, Billing' },
];

// Define the form schema
const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  role: z.enum(['Member', 'Admin', 'Owner']),
});

interface Props {
  organizationId: string;
}

function CreateNewOrgInvite({ organizationId }: Props) {
  // query client
  const queryClient = useQueryClient();

  // state for the role dropdown
  const [isOpen, setIsOpen] = useState(false);

  // Define the form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  // mutation to create a new organization invite
  const addOrgMember = useMutation({
    mutationFn: async (payload: OrganizationInviteRequest) => {
      // call the api to add a new invite
      const res = await OrganizationService.addMember(organizationId, payload);

      return res;
    },
    onSuccess: data => {
      // do something with the data

      // reset the form
      form.reset();

      // invalidate some queries here so that they are refetched
      queryClient.invalidateQueries({
        queryKey: ['getOrgInvites', organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['getOrgMembers', organizationId],
      });

      // show a toast message
      toast(data.message, {
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Define the submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // run the mutation
    addOrgMember.mutateAsync({
      email: values.email,
      name: values.email,
      role: values.role,
    });
  }

  return (
    <Card className="">
      <CardHeader>
        <CardTitle>Invite member</CardTitle>
        <CardDescription>
          Invite a new member to this organization
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Role</FormLabel>
                  <Popover
                    open={isOpen}
                    onOpenChange={o => {
                      setIsOpen(o);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            ' justify-between',
                            !field.value && 'text-muted-foreground',
                          )}
                        >
                          {field.value
                            ? orgRoles.find(r => r.value === field.value)?.value
                            : 'Select Role'}
                          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="p-0">
                      <Command>
                        <CommandInput placeholder="Search Role..." />
                        <CommandEmpty>No role found.</CommandEmpty>
                        <CommandGroup>
                          {orgRoles.map(r => (
                            <CommandItem
                              value={r.value}
                              key={r.value}
                              onSelect={() => {
                                form.setValue('role', r.value);
                                setIsOpen(false);
                              }}
                              className=""
                            >
                              <CheckIcon
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  r.value === field.value
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              <div className="flex flex-col items-start">
                                <p>{r.value}</p>
                                <p className="text-sm text-muted-foreground">
                                  {r.description}
                                </p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-row-reverse justify-between">
              <Button type="submit" disabled={addOrgMember.isPending}>
                {addOrgMember.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Submit
              </Button>

              <Button
                variant="ghost"
                onClick={e => {
                  e.preventDefault();
                  form.reset();
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

export default CreateNewOrgInvite;
